var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { isImageFile, getFileTypeIcon, getMimeTypeFromFilename, formatFileSize } from '../../utility/formatting';
import { useSortable } from '../../hooks/useSortable';
import Lightbox from '../ui/lightbox';
import Img from '../ui/img';
// Default per-file ceiling. Attachments are posted as base64 JSON (~33%
// inflation), so this must leave headroom under the server's request body
// limit (Kestrel default is 30 MB → 20 MB raw file is the safe ceiling).
export const DEFAULT_MAX_FILE_BYTES = 20 * 1024 * 1024;
export var MimeType;
(function (MimeType) {
    MimeType["Png"] = "image/png";
    MimeType["Jpeg"] = "image/jpeg";
    MimeType["Jpg"] = "image/jpg";
    MimeType["Gif"] = "image/gif";
    MimeType["Webp"] = "image/webp";
    MimeType["Pdf"] = "application/pdf";
    MimeType["PlainText"] = "text/plain";
    MimeType["Csv"] = "text/csv";
    MimeType["Xls"] = "application/vnd.ms-excel";
    MimeType["Xlsx"] = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    MimeType["Doc"] = "application/msword";
    MimeType["Docx"] = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
})(MimeType || (MimeType = {}));
const expander = (tuples) => {
    const map = {};
    for (const [types, handler] of tuples) {
        for (const type of (Array.isArray(types) ? types : [types])) {
            map[type] = handler;
        }
    }
    return map;
};
export const resolveHandler = (filename, handlers) => {
    return handlers[getMimeTypeFromFilename(filename)];
};
export const useFilePreview = (file) => {
    const [preview, setPreview] = React.useState(null);
    React.useEffect(() => {
        if (!isImageFile(file.name)) {
            return;
        }
        let objectUrl = null;
        file.arrayBuffer().then((buffer) => {
            objectUrl = URL.createObjectURL(new Blob([buffer], { type: getMimeTypeFromFilename(file.name) }));
            setPreview(objectUrl);
        });
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [file]);
    return preview;
};
export const FilePreviewCard = (props) => {
    const { file, dropdownActions = [], maxPreviewSize = 200, imageUrl, isPrimary } = props;
    const preview = useFilePreview(file);
    const isImage = isImageFile(file.name);
    const fileIcon = getFileTypeIcon(file.name);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [lightboxOpen, setLightboxOpen] = React.useState(false);
    const menuRef = React.useRef(null);
    React.useEffect(() => {
        if (!menuOpen)
            return;
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [menuOpen]);
    const visibleDropdownActions = dropdownActions.filter(({ hidden }) => !hidden);
    return (_jsxs("div", { className: `file-preview-card${isPrimary ? ' file-preview-primary' : ''}`, children: [visibleDropdownActions.length > 0 && (_jsxs("div", { className: "file-preview-actions", ref: menuRef, children: [_jsx("button", { type: "button", className: "file-preview-menu-btn", onClick: () => setMenuOpen(!menuOpen), "aria-label": "File actions", children: _jsx("i", { className: "bi bi-three-dots-vertical" }) }), menuOpen && (_jsx("div", { className: "file-preview-dropdown", children: visibleDropdownActions.map((action, i) => (_jsxs("button", { type: "button", className: action.className, disabled: action.disabled, onClick: () => { action.onClick(); setMenuOpen(false); }, children: [_jsx("i", { className: `bi bi-${action.icon} me-2` }), action.label] }, i))) }))] })), isImage && (preview || imageUrl) ? (imageUrl ? (_jsx(Img, { src: imageUrl, alt: file.name, className: "file-preview-image", style: { maxHeight: `${maxPreviewSize}px`, cursor: 'pointer' }, onClick: () => setLightboxOpen(true) })) : (_jsx("img", { src: preview, alt: file.name, className: "file-preview-image", style: { maxHeight: `${maxPreviewSize}px`, cursor: 'pointer' }, onClick: () => setLightboxOpen(true) }))) : (_jsx("div", { className: "file-preview-icon", style: { height: `${maxPreviewSize}px` }, children: _jsx("i", { className: fileIcon }) })), props.renderInfo && props.renderInfo(file), _jsx(Lightbox, { images: lightboxOpen ? [{ url: imageUrl || preview }] : [], index: 0, alt: file.name, onClose: () => setLightboxOpen(false), onPrev: () => { }, onNext: () => { } })] }));
};
export const FileInputWithPreviews = (props) => {
    var _a, _b;
    const { label = 'Drop files or click to browse', value, onUpload, onRemove, accept, maxFiles, maxFileBytes = DEFAULT_MAX_FILE_BYTES, disabled = false, invalid, size = 'md', className = '', handlers = [], } = props;
    const multiple = (_a = props.multiple) !== null && _a !== void 0 ? _a : true;
    const onReorder = multiple ? props.onReorder : undefined;
    const sortableEnabled = multiple && !!onReorder && !disabled;
    const sortable = useSortable({
        items: value.map((x, i) => {
            x.id = String(i);
            return x;
        }),
        id: (x) => x.id,
        onReorder: (xs) => onReorder === null || onReorder === void 0 ? void 0 : onReorder(xs),
        disabled: !sortableEnabled,
    });
    const inputRef = React.useRef(null);
    const [dragOver, setDragOver] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [sizeErrors, setSizeErrors] = React.useState([]);
    const inputId = React.useId();
    const handlerMap = expander(handlers);
    const acceptedTypes = (_b = accept !== null && accept !== void 0 ? accept : Object.keys(handlerMap)) !== null && _b !== void 0 ? _b : Object.keys(MimeType).join(',');
    const acceptString = acceptedTypes.join(',');
    const buildActions = (index, handler) => ({
        remove: () => { var _a, _b; return (_b = ((_a = handler === null || handler === void 0 ? void 0 : handler.onRemove) !== null && _a !== void 0 ? _a : onRemove)) === null || _b === void 0 ? void 0 : _b(index); },
    });
    const buildDefaultDropdownActions = (actions) => [
        { label: 'Delete', icon: 'trash', onClick: actions.remove, className: 'text-danger' },
    ];
    const handleFiles = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (!files || files.length === 0)
            return;
        const fileArray = Array.from(files);
        const slotsAvailable = maxFiles ? maxFiles - value.length : fileArray.length;
        if (slotsAvailable <= 0)
            return;
        setIsProcessing(true);
        try {
            const processed = [];
            const oversizeErrors = [];
            for (const file of fileArray.slice(0, slotsAvailable)) {
                if (!acceptedTypes.includes(getMimeTypeFromFilename(file.name)))
                    continue;
                if (file.size > maxFileBytes) {
                    oversizeErrors.push(`${file.name} is ${formatFileSize(file.size)}. Maximum file size is ${formatFileSize(maxFileBytes)}.`);
                    continue;
                }
                let customFile = file;
                const handler = resolveHandler(customFile.name, handlerMap);
                if (handler === null || handler === void 0 ? void 0 : handler.processSteps) {
                    let cancelled = false;
                    for (const step of handler.processSteps) {
                        const result = yield step(customFile);
                        if (result === null) {
                            cancelled = true;
                            break;
                        }
                        customFile = result;
                    }
                    if (cancelled)
                        continue;
                }
                processed.push(customFile);
            }
            setSizeErrors(oversizeErrors);
            if (processed.length > 0) {
                yield (onUpload === null || onUpload === void 0 ? void 0 : onUpload(processed));
            }
        }
        finally {
            setIsProcessing(false);
        }
    });
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (disabled || isProcessing)
            return;
        handleFiles(e.dataTransfer.files);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isProcessing)
            setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };
    const handleClick = () => {
        var _a;
        if (!disabled && !isProcessing)
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const dropZoneClasses = [
        'file-input-drop-zone',
        dragOver && 'drag-over',
        disabled && 'disabled',
        size && `file-input-${size}`,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const allErrors = [...sizeErrors, ...(invalid !== null && invalid !== void 0 ? invalid : [])];
    const hasError = allErrors.length > 0;
    return (_jsxs("div", { className: "file-input-container", children: [value.length === 0 ? (_jsxs("div", { className: dropZoneClasses, onClick: handleClick, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, role: "button", tabIndex: disabled ? -1 : 0, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick();
                    }
                }, children: [_jsx("div", { className: "file-input-icon mb-2", children: _jsx("i", { className: "bi bi-cloud-upload", style: { fontSize: '3rem', color: 'var(--muted)' } }) }), _jsx("div", { className: "file-input-label", children: label }), multiple && maxFiles && (_jsxs("div", { className: "text-muted mt-2", style: { fontSize: '0.875rem' }, children: ["Maximum ", maxFiles, " files"] })), _jsxs("div", { className: "text-muted mt-1", style: { fontSize: '0.875rem' }, children: ["Maximum file size: ", formatFileSize(maxFileBytes)] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: `file-input-drop-zone-small ${dragOver ? 'drag-over' : ''} ${disabled || isProcessing ? 'disabled' : ''}`, onClick: handleClick, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, role: "button", tabIndex: disabled ? -1 : 0, onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleClick();
                            }
                        }, style: {
                            border: '1px dashed var(--border)',
                            borderRadius: '0.375rem',
                            padding: '0.5rem',
                            textAlign: 'center',
                            cursor: disabled || isProcessing ? 'not-allowed' : 'pointer',
                            marginBottom: '1rem',
                        }, children: [_jsx("i", { className: "bi bi-plus-circle me-2" }), isProcessing ? 'Processing...' : multiple ? 'Add more files' : 'Replace file'] }), _jsx("div", { className: "file-preview-grid", children: sortable.order.map((file, index) => {
                            const handler = resolveHandler(file.name, handlerMap);
                            const actions = buildActions(index, handler);
                            const preview = (handler === null || handler === void 0 ? void 0 : handler.renderPreview)
                                ? handler.renderPreview(file, actions, index)
                                : (_jsx(FilePreviewCard, { file: file, actions: actions, dropdownActions: buildDefaultDropdownActions(actions) }));
                            if (!sortableEnabled) {
                                return _jsx(React.Fragment, { children: preview }, `${file.name}-${index}`);
                            }
                            return (_jsxs("div", Object.assign({}, sortable.getItemProps(file.id), { className: `sortable-item${sortable.draggingId === file.id ? ' sortable-dragging' : ''}`, style: { position: 'relative' }, children: [_jsx("div", Object.assign({ className: "sortable-drag-handle sortable-drag-handle--overlay", title: "Drag to reorder" }, sortable.getHandleProps(file.id), { children: _jsx("i", { className: "bi bi-grip-vertical" }) })), preview] }), file.id));
                        }) })] })), _jsx("input", { ref: inputRef, id: inputId, type: "file", accept: acceptString, multiple: multiple, onChange: (e) => {
                    handleFiles(e.target.files);
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                }, disabled: disabled || isProcessing, style: { display: 'none' } }), hasError && (_jsx("div", { className: "invalid-feedback d-block mt-2", children: allErrors.map((error, i) => (_jsx("div", { children: error }, i))) }))] }));
};
export default FileInputWithPreviews;
